export interface CustomEventSourceEvent {
  data: string; // The raw JSON string
  parsed?: any; // Optional: the parsed JSON object
}

export class CustomEventSource {
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private decoder = new TextDecoder();
  private buffer = '';
  private abortController: AbortController;
  private isClosed = false;

  onMessage: ((event: CustomEventSourceEvent) => void) | null = null;
  onError: ((error: Event) => void) | null = null;
  onOpen: (() => void) | null = null;
  onClose: (() => void) | null = null; // ✅ Add onClose callback

  constructor(
    private url: string,
    private options: { headers?: Record<string, string> } = {}
  ) {
    this.abortController = new AbortController();
    this.connect();
  }

  private async connect() {
    try {
      const response = await fetch(this.url, {
        headers: this.options.headers,
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (this.onOpen) {
        this.onOpen();
      }

      this.reader = response.body?.getReader() || null;
      if (!this.reader) {
        throw new Error('No reader available');
      }

      // Start reading the stream
      this.readStream();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return; // Clean abort, don't call onerror
      }
      if (this.onError) {
        this.onError(error);
      }
    }
  }

  private async readStream() {
    if (!this.reader) return;

    try {
      while (true) {
        const { done, value } = await this.reader.read();

        if (done) {
          console.log('✅ [CustomEventSource] Stream finished (done=true)');

          // ✅ Process any remaining buffer before closing
          if (this.buffer.trim()) {
            console.log('📝 [CustomEventSource] Processing remaining buffer:', this.buffer);
            const lines = this.buffer.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const jsonData = line.slice(6).trim();
                if (jsonData && this.onMessage) {
                  try {
                    const parsed = JSON.parse(jsonData);
                    this.onMessage({
                      data: jsonData,
                      parsed: parsed
                    });
                  } catch (parseError) {
                    console.warn('Failed to parse remaining SSE event:', parseError);
                  }
                }
              }
            }
          }

          // ✅ Automatically close when stream is done
          this.close();
          break;
        }

        // Decode chunk and add to buffer
        this.buffer += this.decoder.decode(value, { stream: true });

        // Split by newlines, keeping incomplete lines in buffer
        const lines = this.buffer.split('\n');
        this.buffer = lines.pop() || '';

        // Process complete lines
        for (const line of lines) {
          // SSE format: "data: <json>\n\n"
          if (line.startsWith('data: ')) {
            const jsonData = line.slice(6).trim();
            if (jsonData && this.onMessage) {
              try {
                // Parse the JSON to extract type
                const parsed = JSON.parse(jsonData);

                // NestJS wraps it as: { data: MassMailStreamEvent, type: string }
                // Extract the type from the MessageEvent wrapper
                this.onMessage({
                  data: jsonData, // Keep raw JSON string
                  parsed: parsed // Provide parsed data for convenience
                });
              } catch (parseError) {
                // If parsing fails, still emit with raw data
                console.warn('Failed to parse SSE event:', parseError);
                this.onMessage({
                  data: jsonData
                });
              }
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError' && this.onError) {
        this.onError(error);
      }
    }
  }

  close() {
    if (this.isClosed) {
      return; // Already closed
    }

    console.log('🧹 [CustomEventSource] Closing connection...');
    this.isClosed = true;

    this.abortController.abort();
    if (this.reader) {
      this.reader.cancel();
      this.reader = null;
    }

    // ✅ Call onClose callback when stream is finished
    if (this.onClose) {
      console.log('✅ [CustomEventSource] Calling onClose callback');
      this.onClose();
    }
  }
}
